import { type RefObject, useCallback, useLayoutEffect, useState } from "react";
import { getSiteHeaderStickyTop } from "@/lib/siteHeaderOffset";

/**
 * Schwebender Tabellenkopf (Portal auf `document.body`).
 * Unter Site-Header (`z-40`) und Modals (`z-50`+), über normalem Seiteninhalt.
 * Früher `z-[100]` – dadurch lagen Köpfe über Dialogen.
 */
export const FLOATING_TABLE_HEADER_Z_CLASS = "z-30";

/** Entspricht `getOrdersStickyTopOffset` / `refreshOrdersHeaderStickyState` in `auftraege.js`. */
export interface FloatingProjectTableHeaderState {
  top: number;
  left: number;
  width: number;
  scrollLeft: number;
  tableWidth: number;
}

/**
 * Fixierter Header-Klon beim Seiten-Scroll (Portal) – nicht `position: sticky` auf
 * `th`/Grid-Zellen, das mit overflow-x oder fixierten Aktionsspalten kollidiert.
 */
export function useFloatingStickyHeader(
  scrollRootRef: RefObject<HTMLElement | null>,
  anchorRef: RefObject<HTMLElement | null>,
  getHeaderEl: () => HTMLElement | null,
  options: { enabled: boolean; syncKey: string },
): FloatingProjectTableHeaderState | null {
  const [state, setState] = useState<FloatingProjectTableHeaderState | null>(null);

  useLayoutEffect(() => {
    if (!options.enabled) {
      setState(null);
      return;
    }

    const scrollRoot = scrollRootRef.current;
    const anchor = anchorRef.current;
    if (!scrollRoot || !anchor) {
      setState(null);
      return;
    }

    const update = () => {
      const stickyTop = getSiteHeaderStickyTop();
      const headerEl = getHeaderEl();
      const anchorRect = anchor.getBoundingClientRect();
      const headHeight = headerEl?.getBoundingClientRect().height ?? 0;

      const isStuck =
        anchorRect.top <= stickyTop + 0.5 && anchorRect.bottom > stickyTop + headHeight + 2;

      if (!isStuck) {
        setState(null);
        return;
      }

      const scrollRootRect = scrollRoot.getBoundingClientRect();
      const next: FloatingProjectTableHeaderState = {
        top: stickyTop,
        left: scrollRootRect.left,
        width: scrollRootRect.width,
        scrollLeft: scrollRoot.scrollLeft,
        tableWidth: anchorRect.width,
      };
      setState((prev) => {
        if (
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.scrollLeft === next.scrollLeft &&
          prev.tableWidth === next.tableWidth
        ) {
          return prev;
        }
        return next;
      });
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    scrollRoot.addEventListener("scroll", update, { passive: true });

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      scrollRoot.removeEventListener("scroll", update);
    };
  }, [options.enabled, options.syncKey, scrollRootRef, anchorRef, getHeaderEl]);

  return state;
}

/**
 * Gleiche Idee wie die klassische Projekte-Seite: bei vertikalem Scrollen wird ein
 * fixiertes Kopie-`thead` über der horizontal scrollbaren Tabelle angezeigt.
 */
export function useFloatingProjectTableHeader(
  wrapRef: RefObject<HTMLDivElement | null>,
  tableRef: RefObject<HTMLTableElement | null>,
  options: { enabled: boolean; syncKey: string },
): FloatingProjectTableHeaderState | null {
  const getHeaderEl = useCallback(
    () => tableRef.current?.querySelector("thead") ?? null,
    [tableRef],
  );
  return useFloatingStickyHeader(
    wrapRef,
    tableRef as RefObject<HTMLElement | null>,
    getHeaderEl,
    options,
  );
}
